import React, { useState } from 'react';
import { Mail, Phone, Globe, MapPin, Pencil, Images, Play, Trash2, ChevronDown, ChevronUp, ExternalLink, Briefcase, Building } from 'lucide-react';
import { BioRichTextDisplay } from './BioMediaDisplay';
import { BrandIcons, getSocialLinks, getExtraLinks, extractYouTubeVideoId } from './BrandIcons';
import { GalleryGrid } from './GalleryLightbox';

const BioTemplateProfessional = ({ card, bioUrl, constructSocialUrl, trackLinkClick, onQRClick, hideQR = false, isOwner = false, onUpdateProfileImage, onUpdateLogo, isPremium = false }) => {
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const hasGallery = card.galleryImages && card.galleryImages.length > 0;
  const hasVideos = card.youtubeVideos && card.youtubeVideos.length > 0;
  const socialLinks = getSocialLinks(card);
  const extraLinks = getExtraLinks(card);
  const accentColor = card.qrColor || '#1e40af';

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="h-28" style={{ backgroundColor: accentColor }}></div>
        
        <div className="bg-white shadow-lg -mt-16 mx-4 rounded-lg overflow-visible">
          <div className="px-5 pb-6">
            {/* Profile - Large Image */}
            <div className="flex flex-col items-center -mt-16 mb-4">
              <div className="relative">
                {card.profileImage ? (
                  <img src={card.profileImage} alt={card.name} className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover" />
                ) : (
                  <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg flex items-center justify-center" style={{ backgroundColor: accentColor }}>
                    <span className="text-4xl font-bold text-white">{card.name?.charAt(0) || 'U'}</span>
                  </div>
                )}
                {isOwner && onUpdateProfileImage && (
                  <label htmlFor="profile-upload-professional" className="absolute bottom-2 right-2 bg-white rounded-full p-1.5 shadow cursor-pointer hover:bg-blue-50 border">
                    <Pencil className="w-3.5 h-3.5 text-blue-600" />
                    <input id="profile-upload-professional" type="file" accept="image/*" onChange={onUpdateProfileImage} className="hidden" />
                  </label>
                )}
              </div>
              <h1 className="text-lg font-bold text-gray-900 mt-3">{card.name}</h1>
              {card.title && <p className="text-sm text-gray-600 flex items-center gap-1"><Briefcase className="w-3 h-3" />{card.title}</p>}
              {card.company && <p className="text-xs text-gray-500 flex items-center gap-1"><Building className="w-3 h-3" />{card.company}</p>}
            </div>

            {/* Contact */}
            <div className="space-y-2 mb-5">
              {card.phone && (
                <a href={`tel:${card.phone}`} onClick={() => trackLinkClick('phone', card.phone)} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <Phone className="w-4 h-4" style={{ color: accentColor }} /><span className="text-gray-700 text-sm">{card.phone}</span>
                </a>
              )}
              {card.email && (
                <a href={`mailto:${card.email}`} onClick={() => trackLinkClick('email', card.email)} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <Mail className="w-4 h-4" style={{ color: accentColor }} /><span className="text-gray-700 text-sm break-all">{card.email}</span>
                </a>
              )}
              {(card.website || (card.websiteName && card.websiteTLD)) && (
                <a href={card.website || `https://${card.websiteName}${card.websiteTLD}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <Globe className="w-4 h-4" style={{ color: accentColor }} /><span className="text-gray-700 text-sm break-all">{(card.website || `${card.websiteName}${card.websiteTLD}`).replace(/^https?:\/\//, '')}</span>
                </a>
              )}
              {(card.location || card.locationAddress) && (
                <a href={
                  card.location && (card.location.startsWith('http://') || card.location.startsWith('https://'))
                    ? card.location 
                    : `https://maps.google.com/?q=${encodeURIComponent(card.locationAddress || card.location || '')}`
                } target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <MapPin className="w-4 h-4" style={{ color: accentColor }} /><span className="text-gray-700 text-sm">{card.locationAddress || card.location || 'View Location'}</span>
                </a>
              )}
            </div>

            {/* Social */}
            {socialLinks.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-5">
                {socialLinks.map((social, index) => {
                  const BrandIcon = BrandIcons[social.type];
                  return (
                    <a key={index} href={constructSocialUrl(social.type, social.value)} target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick(social.type, social.value)} className="w-10 h-10 rounded-lg bg-slate-50 hover:bg-slate-100 flex items-center justify-center transition-all hover:scale-110" title={social.label}>
                      {BrandIcon && <BrandIcon size={18} />}
                    </a>
                  );
                })}
              </div>
            )}

            {/* Extra Links */}
            {extraLinks.length > 0 && (
              <div className="space-y-2 mb-5">
                {extraLinks.map((link, index) => (
                  <a key={index} href={link.url} target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick('extraLink', link.url)} className="flex items-center gap-3 p-2.5 rounded-lg transition-colors" style={{ backgroundColor: `${accentColor}15`, borderLeft: `3px solid ${accentColor}` }}>
                    <ExternalLink className="w-4 h-4" style={{ color: accentColor }} /><span className="text-gray-700 text-sm font-medium">{link.title}</span>
                  </a>
                ))}
              </div>
            )}

            {/* Bio */}
            {card.richBio && (
              <div className="bg-slate-50 rounded-lg p-4 mb-5">
                <div className={`overflow-hidden transition-all duration-300 text-sm text-gray-600 ${!isBioExpanded ? 'max-h-12' : 'max-h-[2000px]'}`}>
                  <BioRichTextDisplay content={card.richBio} />
                </div>
                <button onClick={() => setIsBioExpanded(!isBioExpanded)} className="flex items-center gap-1 mt-2 text-xs font-medium" style={{ color: accentColor }}>
                  {isBioExpanded ? <><ChevronUp className="w-3.5 h-3.5" />Show less</> : <><ChevronDown className="w-3.5 h-3.5" />Read more</>}
                </button>
              </div>
            )}

            {/* Gallery */}
            {hasGallery && (
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-3"><Images className="w-4 h-4 text-gray-500" /><h3 className="text-sm font-semibold text-gray-700">Gallery</h3></div>
                <GalleryGrid 
                  images={card.galleryImages} 
                  onDeleteImage={card.onDeleteImage} 
                  isOwner={isOwner}
                  accentColor={accentColor}
                />
              </div>
            )}

            {/* Videos */}
            {hasVideos && (
              <div>
                <div className="flex items-center gap-2 mb-3"><Play className="w-4 h-4 text-gray-500" /><h3 className="text-sm font-semibold text-gray-700">Videos</h3></div>
                <div className="space-y-2">
                  {card.youtubeVideos.slice(0, 3).map((videoId, index) => {
                    const cleanVideoId = extractYouTubeVideoId(videoId);
                    return (
                      <div key={index} className="relative aspect-video rounded-lg overflow-hidden group">
                        <iframe src={`https://www.youtube.com/embed/${cleanVideoId}?rel=0`} title={`Video ${index + 1}`} className="w-full h-full" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                        {isOwner && card.onDeleteVideo && <button onClick={() => card.onDeleteVideo(index)} className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"><Trash2 className="w-3 h-3" /></button>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BioTemplateProfessional;
